import { Box, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ASSETS_BASE_URL } from '../../config';

const ImageBoxCard = ({
    img = `${ASSETS_BASE_URL}/ImageBoxes/imageBox.jpg`, // Default image if none provided
    title = "Home",
    href = "#",
    external = false,
    onClick = null, //   Added onClick prop
    state = null, //   Added state prop for navigation
    badge = null, // Count shown in a circle at the top-right corner
    active = false // Highlights the card (red border/title) when its panel is open
}) => {
    const navigate = useNavigate();
    const fallbackImg = `${ASSETS_BASE_URL}/ImageBoxes/imageBox.jpg`; // Change this to your real fallback image
    const [imgSrc, setImgSrc] = useState(img);

    const handleImageError = () => {
        if (imgSrc !== fallbackImg) setImgSrc(fallbackImg);
    };

    const handleClick = () => {
        //   Priority 1: If onClick function is provided, use it
        if (onClick) {
            onClick();
            return;
        }

        //   Priority 2: If href is provided
        if (!href || href === "#") return;

        if (external) {
            window.open(href, "_blank", "noopener,noreferrer");
        } else {
            //   Navigate with state if provided
            if (state) {
                navigate(href, { state });
            } else {
                navigate(href);
            }
        }
    };

    return (
        <Box
            onClick={handleClick}
            sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '10px',
                boxShadow: '0 0 6px 1px rgba(0,0,0,0.1)',
                border: active ? '2px solid var(--primary-main, #ed1f27)' : '2px solid transparent',
                width: '100%',
                maxWidth: '250px',
                aspectRatio: '1 / 1',
                minWidth: 0,
                overflow: 'hidden',
                transition: 'transform 0.1s ease-in-out',
                cursor: (onClick || href) ? 'pointer' : 'default',
                '&:hover': {
                    boxShadow: '0 0 6px 1px rgba(0,0,0,0.2)',
                    transform: 'scale(1.02)',
                },
            }}
        >
            {badge !== null && badge !== undefined && badge > 0 && (
                <Box sx={{
                    position: 'absolute',
                    // Positive offsets, not negative — the card below has overflow:hidden for its
                    // rounded corners, so a badge hung off the edge (top/right: -8px) was clipped away.
                    top: '8px',
                    right: '8px',
                    zIndex: 1,
                    minWidth: '28px',
                    height: '28px',
                    px: '6px',
                    borderRadius: '14px',
                    backgroundColor: 'var(--primary-main, #ed1f27)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px',
                    fontWeight: 700,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                }}>
                    {badge}
                </Box>
            )}
            {/* Image Box */}
            <Box
                sx={{
                    width: '100%',
                    flex: '1 1 auto',
                    minHeight: 0,
                    overflow: 'hidden',
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                }}
            >
                <Box
                    component="img"
                    src={imgSrc}
                    alt={title}
                    onError={handleImageError}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.1s ease-in-out',
                        '&:hover': {
                            transform: (onClick || href) ? 'scale(1.01)' : 'none',
                        },
                    }}
                />
            </Box>

            {/* Title Box */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '45px',
                    flexShrink: 0,
                    px: 1,
                    backgroundColor: '#fff',
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        width: '100%',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2, //   Allows up to 2 lines before ellipsis
                        WebkitBoxOrient: 'vertical',
                        textTransform: 'uppercase',
                        lineHeight: 1.2,
                        fontWeight: "bold",
                        color: active ? 'var(--primary-main, #ed1f27)' : 'inherit',
                    }}
                >
                    {title}
                </Typography>
            </Box>
        </Box>
    );
};

export default ImageBoxCard;
